import { useState } from 'react';
import { TextField, Button, Typography, Box, Stack, InputAdornment, IconButton, Link } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom'; // ✅ Consolidated imports

// ✅ FIXED: Named imports from root for icons
import { Visibility, VisibilityOff } from '@mui/icons-material';

import app, { analytics, logEvent } from '../../services/firebase';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";

function AuthForm() {
    const auth = getAuth(app);
    const navigate = useNavigate();
    const db = getFirestore(app);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoginForm, setIsLoginForm] = useState(true);       // false => register
    const [error, setError] = useState(null);    // State for error messages

    // const userProfile = {
    //     email: user.email,
    //     displayName: user.displayName || `User-${user.uid.substring(0,5)}`,
    //     userId: user.email.split('@')[0],
    //     photoURL: user.photoURL || null,
    //     createdAt: serverTimestamp(),
    //     roles: ['author'],  // ← default new users as author, or [] and assign manually
    //     // isAdmin: false,  // optional separate flag
    // };

    const handleEmailChange = (event) => {
        setEmail(event.target.value)
    };

    const handlePasswordChange = (event) => {
        setPassword(event.target.value)
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null); // Clear any previous errors

        try {
            if (isLoginForm) {
                // Sign in existing user
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                logEvent(analytics, 'login', { method: 'email' });
                navigate('/contactregistrylist')

            } else {
                // Create new user
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                // Now, create the user profile document in Firestore
                const userProfile = {
                    email: user.email, // from Auth
                    displayName: user.displayName || `User ${user.uid.substring(0, 5)}`, // from Auth, with fallback
                    userId: email.split('@')[0], // Use the part before the @ as the initial userId
                    photoURL: user.photoURL || null, // from Auth
                    createdAt: serverTimestamp(),
                    // Add any other default profile fields here
                    roles: ['author']
                };

                // Use the UID from Auth as the document ID in Firestore
                await setDoc(doc(db, "users", user.uid), userProfile);
                logEvent(analytics, 'sign_up', { method: 'email' });
                navigate('/account'); // Navigate to their new account/profile page

            }
            // Navigate after successful login/registration
        } catch (error) {
            logEvent(analytics, 'auth_error', {
                auth_method: isLoginForm ? 'login' : 'register',
                error_code: error.code,
                error_message: error.message
            });

            switch (error.code) {
                case 'auth/email-already-in-use':
                    setError("An account with this email already exists. Try signing in instead.");
                    break;
                case 'auth/invalid-email':
                    setError('Please provide a valid email.');
                    break;
                case 'auth/wrong-password':
                    // actually password, but more secure if obscure about uid too.
                    setError('Incorrect email or password.');   
                    break;
                case 'auth/user-not-found':
                    setError('No user found with this email.');
                    break;
                case 'auth/missing-password':
                    setError('Please enter a password.');
                    break;
                case 'auth/weak-password':
                    setError('The password must be 6 characters long or more.');
                    break;
                default:
                    setError('An unexpected error occurred. Please try again.');
            }
            setPassword('')
        }

    };

    const toggleForm = () => {
        setIsLoginForm(!isLoginForm);
        setError(null); // Clear any errors when switching forms
    };


    const handlePasswordReset = async () => {
        try {
            await sendPasswordResetEmail(auth, email);
            alert("Password reset email sent!");
        } catch (error) {
            setError(error.message)
        }
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
                mt: 1,
                mx: 'auto',
                width: '100%',
                maxWidth: {
                    xs: '90%',
                    sm: '80%',
                    md: '75%',
                    lg: '50%',
                    xl: '25%',
                }
            }}
            xs={12} sm={12} md={12} lg={3}>

            <Typography variant="h5" align="center" gutterBottom>
                {isLoginForm ? 'Sign In' : 'Register'}
            </Typography>

            {/* Error Display */}
            {error && (
                <Typography variant="body2" color="error" align="center" sx={{ color: 'red' }}>
                    {error}
                </Typography>
            )}

            <Stack spacing={2} sx={{ mt: 2 }}>

                <TextField label="Email" type="email"
                    required
                    helperText="Please enter a valid email address."
                    fullWidth value={email}
                    onChange={handleEmailChange}
                    onFocus={() => setError(null)}
                />
                <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    fullWidth
                    value={password}
                    onChange={handlePasswordChange}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Conditionally render forgot password link */}
                {isLoginForm && (
                    <Box sx={{ textAlign: 'left' }}>
                        <Link 
                        component={RouterLink} 
                        to="#"
                        variant="body2" 
                        onClick={handlePasswordReset}
                        sx={{ cursor: 'pointer' }}
                        >
                            Forgot password?
                        </Link>
                    </Box>
                )}

                <Button type="submit" variant="contained" fullWidth>{isLoginForm ? 'Sign In' : 'Register'}</Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Link 
                        component={RouterLink} 
                        to
                        variant="body2" 
                        onClick={toggleForm}
                        sx={{ cursor: 'pointer' }}
                    >
                            {isLoginForm ? "Don't have an account? Register" : 'Already have an account? Sign In'}
                    </Link>

                </Box>
            </Stack>

        </Box>
    )

}


export default AuthForm;
